import { useRef, useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  HStack,
  Text,
  Badge,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FiChevronDown, FiEdit2, FiRefreshCw, FiTrash2 } from "react-icons/fi";
import { useSavedFilters } from "../../../hooks/useSavedFilters";
import SaveFilterModal from "./SaveFilterModal";
import {
  showSuccessToast,
  showErrorToast,
  showDeletedToast,
} from "../../../util/errorHandler";

/**
 * Generic Saved Filters UI — reusable by any page that owns a filter-state
 * object and a way to restore one (`applyFilterState`). Never interprets
 * `filterConfig` itself; it only snapshots and restores whatever the page
 * hands it. See docs/superpowers/plans/2026-08-27-generic-saved-filters.md.
 */
const SavedFiltersPanel = ({
  context,
  currentFilterState,
  applyFilterState,
}) => {
  const toast = useToast();
  const {
    mine,
    public: publicFilters,
    loading,
    save,
    update,
    remove,
    apply,
  } = useSavedFilters({
    context,
    applyFilterState,
  });

  const saveModal = useDisclosure();
  const editModal = useDisclosure();
  const deleteDialog = useDisclosure();
  const cancelRef = useRef();

  const [editingFilter, setEditingFilter] = useState(null);
  const [deletingFilter, setDeletingFilter] = useState(null);

  const handleApply = (savedFilter) => {
    apply(savedFilter);
    showSuccessToast(toast, `Applied "${savedFilter.name}"`);
  };

  const handleSaveNew = async (values) => {
    try {
      await save({ ...values, filterConfig: currentFilterState });
      showSuccessToast(toast, "Filter saved");
    } catch (error) {
      showErrorToast(toast, "Failed to save filter");
      throw error;
    }
  };

  const handleUpdateFilterConfig = async (savedFilter) => {
    try {
      await update(savedFilter._id, { filterConfig: currentFilterState });
      showSuccessToast(
        toast,
        `Updated "${savedFilter.name}" with your current filters`,
      );
    } catch (error) {
      showErrorToast(toast, "Failed to update filter");
    }
  };

  const openEdit = (savedFilter) => {
    setEditingFilter(savedFilter);
    editModal.onOpen();
  };

  const handleSaveEdit = async (values) => {
    try {
      await update(editingFilter._id, values);
      showSuccessToast(toast, "Filter updated");
    } catch (error) {
      showErrorToast(toast, "Failed to update filter");
      throw error;
    }
  };

  const openDelete = (savedFilter) => {
    setDeletingFilter(savedFilter);
    deleteDialog.onOpen();
  };

  const confirmDelete = async () => {
    try {
      await remove(deletingFilter._id);
      showDeletedToast(toast);
    } catch (error) {
      showErrorToast(toast, "Failed to delete filter");
    } finally {
      deleteDialog.onClose();
      setDeletingFilter(null);
    }
  };

  const hasSavedFilters = mine.length > 0 || publicFilters.length > 0;

  return (
    <Box>
      <Menu closeOnSelect={false}>
        <MenuButton
          as={Button}
          size="sm"
          variant="outline"
          rightIcon={<FiChevronDown />}
          isLoading={loading}
        >
          Saved Filters
          {hasSavedFilters ? ` (${mine.length + publicFilters.length})` : ""}
        </MenuButton>
        <MenuList maxH="320px" overflowY="auto" minW="300px" zIndex="popover">
          {!hasSavedFilters && (
            <MenuItem isDisabled>No saved filters yet</MenuItem>
          )}

          {mine.length > 0 && (
            <MenuGroup title="My Saved Filters">
              {mine.map((savedFilter) => (
                <Box
                  key={savedFilter._id}
                  px={3}
                  py={2}
                  _hover={{ bg: "gray.50" }}
                >
                  <HStack
                    justifyContent="space-between"
                    overflow="hidden"
                    cursor="pointer"
                    onClick={() => handleApply(savedFilter)}
                  >
                    <Text noOfLines={1} fontWeight="500">
                      {savedFilter.name}
                    </Text>
                    {savedFilter.visibility === "public" && (
                      <Badge colorScheme="green" fontSize="10px">
                        public
                      </Badge>
                    )}
                  </HStack>
                  <HStack spacing={3} mt={1}>
                    <Button
                      size="xs"
                      variant="link"
                      leftIcon={<FiRefreshCw />}
                      title="Replace this saved filter's criteria with whatever's currently selected on the page"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateFilterConfig(savedFilter);
                      }}
                    >
                      Update filters
                    </Button>
                    <Button
                      size="xs"
                      variant="link"
                      leftIcon={<FiEdit2 />}
                      title="Rename or change visibility only — does not change the filter criteria"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEdit(savedFilter);
                      }}
                    >
                      Rename
                    </Button>
                    <Button
                      size="xs"
                      variant="link"
                      colorScheme="red"
                      leftIcon={<FiTrash2 />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDelete(savedFilter);
                      }}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Box>
              ))}
            </MenuGroup>
          )}

          {publicFilters.length > 0 && (
            <MenuGroup title="Public Saved Filters">
              {publicFilters.map((savedFilter) => (
                <MenuItem
                  key={savedFilter._id}
                  onClick={() => handleApply(savedFilter)}
                >
                  <Text noOfLines={1}>{savedFilter.name}</Text>
                </MenuItem>
              ))}
            </MenuGroup>
          )}

          <MenuDivider />
          <MenuItem onClick={saveModal.onOpen} fontWeight="600">
            + Save current filter
          </MenuItem>
        </MenuList>
      </Menu>

      <SaveFilterModal
        isOpen={saveModal.isOpen}
        onClose={saveModal.onClose}
        onSave={handleSaveNew}
        toast={toast}
      />

      {editingFilter && (
        <SaveFilterModal
          isOpen={editModal.isOpen}
          onClose={() => {
            editModal.onClose();
            setEditingFilter(null);
          }}
          onSave={handleSaveEdit}
          toast={toast}
          initialValues={editingFilter}
          title="Rename saved filter"
          submitLabel="Save changes"
        />
      )}

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete saved filter</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete &quot;{deletingFilter?.name}
              &quot;? This cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SavedFiltersPanel;
